import AcRefundModal from "@molecules/ac-refund-modal/ac-refund-modal";
import { useState, useMemo, useEffect, useCallback } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

import TransactionHero1x from "@assets/images/transaction-hero-1x.png";

import {
  AcTooltip,
  AcAccordion,
  AcDivider,
  DATETIME_FORMATS,
  ICONS,
  KEYS,
  THEMES,
  AcIsArray,
} from "@opp/ui-kit";

import { AcIsSet, AcUUID, AcSanitize } from "@opp/ui-kit";
import { AcFormatCurrencyAmount, AcFormatDate } from "@utils";

import {
  AcContainer,
  AcRow,
  AcColumn,
  AcHeading,
  AcRichContent,
  AcCard,
  AcEmptyBlock,
  AcImage,
  AcBadge,
  AcButton,
  AcIcon,
  AcDefinitionList,
} from "@opp/ui-kit";
import AcCopyText from "@atoms/ac-copy-text/ac-copy-text";
import { withTranslation } from "react-i18next";
import AcTransactionDetailsChangelog from "../changelog/changelog";
import { AcGetPaymentMethodImg } from "../../../utilities/ac-format-payment-method";
import { AcGetDateIsInFuture } from "../../../utilities/ac-format-date";
import moment from "moment/moment";
import { Link } from "react-router-dom";
import { AcFormatInternalURI } from "../../../utilities";

const _CLASSES = {
  MAIN: "ac-transaction-details-details-tab ac-transaction__grid-container",
  PAYMENT_ICON: "ac-transaction__payment-icon",
};

const AcTransactionDetailsDetailsTab = ({
  store: { ui, transactions, profile, settings },
  i18n,
  t,
  data,
}) => {
  const { has_continuous_settlement_interval, configurations } = settings;

  const { current_partner_currency, current_merchant_profiles } = profile;

  const { current_payouts, is_loading_payouts } = transactions;

  const [accordionOpen, setAccordionOpen] = useState(false);

  const getPaymentIconClassNames = useMemo(() => {
    return clsx(_CLASSES.PAYMENT_ICON);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  });

  useEffect(() => {
    if (!AcIsSet(data.uid)) {
      return;
    }
    transactions.get_payouts(t, data.uid);
  }, [data.uid]);

  const displayRefundModal = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t("TITLES.REFUND", "Refund", { ns: "titles" })}`,
      name: "refund-modal",
      body: (
        <AcRefundModal
          transaction={data}
          submitRefund={transactions.store_refund}
          submitTopup={transactions.store_topup}
        />
      ),
      closeable: true,
      visible: true,
      callback: async () => {
        await ui.reset(KEYS.MODAL, KEYS.VISIBLE, false);
      },
    });
  };

  const getRefundButtonOption = useMemo(() => {
    return {
      className: "h-margin-left-10",
      theme: THEMES.OMEGA,
      title: `${t("TITLES.REFUND", "Refund", { ns: "titles" })}`,
      callback: displayRefundModal,
    };
  }, [data]);

  const getPartnerNameBadge = useMemo(() => {
    if (
      !AcIsSet(current_merchant_profiles) ||
      !AcIsSet(current_merchant_profiles.partner)
    ) {
      return null;
    }

    return (
      <div className={"h-margin-top-5"}>
        <AcBadge theme={THEMES.OMEGA_LIGHT}>
          <span className={"h-text--color-pitch"}>
            {current_merchant_profiles.partner.name}
          </span>
        </AcBadge>
      </div>
    );
  }, [current_merchant_profiles]);

  const getPaymentMethodIcon = useMemo(() => {
    if (
      !AcIsSet(data) ||
      (!AcIsSet(data.payment_method) && !AcIsSet(data.parent_payment_method))
    ) {
      return null;
    }

    return AcGetPaymentMethodImg(
      t,
      getPaymentIconClassNames,
      data.payment_method,
      data.parent_payment_method
    );
  }, [data]);

  const getStatusBadge = useMemo(() => {
    if (!AcIsSet(data)) {
      return null;
    }

    const { status } = data;

    let label = t(`status.${status}`, { ns: "transactions" });
    let theme = null;

    switch (status) {
      case "created":
      case "reserved":
      case "pending":
      case "planned":
        theme = THEMES.OMEGA_LIGHT;
        break;

      case "on_hold":
      case "refunded":
        theme = THEMES.ECHO;
        break;

      case "chargeback":
        theme = THEMES.BETA_LIGHT;
        break;

      case "expired":
      case "cancelled":
      case "failed":
      case "revoked":
        theme = THEMES.ERROR;
        break;

      case "completed":
        theme = THEMES.SUCCESS;
        break;

      default:
    }

    return <AcBadge theme={theme}>{label}</AcBadge>;
  }, [data]);

  const renderMainFeatures = useMemo(() => {
    const { amount, currency, status, order } = data;

    const content = AcFormatCurrencyAmount(
      amount,
      currency || profile.current_partner_currency,
      i18n.resolvedLanguage,
      true
    );

    return (
      <AcContainer flex className={"ac-transaction__container"}>
        <AcRow className={"h-margin-bottom-auto"}>
          <AcColumn xs={6} sm={6} md={6}>
            <AcHeading
              rank={2}
              theme={THEMES.DARK}
              className={"ac-transaction__amount"}
            >
              {content}
            </AcHeading>
          </AcColumn>

          <AcColumn
            xs={6}
            sm={6}
            md={6}
            className={"h-text--align-right h-flex-h-align-end"}
          >
            {getPartnerNameBadge}
            {getPaymentMethodIcon}
          </AcColumn>

          {AcIsSet(order) &&
            AcIsSet(order.products) &&
            AcIsArray(order.products) &&
            AcIsSet(order.products[0]) && (
              <AcColumn className={"h-hidden-xxs h-hidden-xs h-hidden-sm"}>
                <AcRichContent
                  content={`<span class="h-text--color-medium h-text--size-medium">${AcSanitize(
                    order.products[0].name
                  )}</span>`}
                />
              </AcColumn>
            )}
        </AcRow>

        {[KEYS.COMPLETED, KEYS.RESERVED].includes(status) &&
          !has_continuous_settlement_interval &&
          configurations.refunds && (
            <AcRow className={"h-margin-top-auto"}>
              <AcButton {...getRefundButtonOption}>
                <AcIcon icon={ICONS.UNDO} />
                <span>
                  {t("refund.refund-start", "Refund", { ns: "transactions" })}
                </span>
              </AcButton>
              <AcTooltip
                content={t("refund-tooltip", { ns: "transactions" })}
                trigger="hover"
                className="h-margin-left-10 h-padding-top-3 d-md-inline d-none"
                placement="top"
              >
                <span>
                  <AcIcon
                    icon={ICONS.INFO_OUTLINED}
                    className={"h-text--color-medium"}
                    style={{ fontSize: "2.6rem" }}
                  />
                </span>
              </AcTooltip>
            </AcRow>
          )}
      </AcContainer>
    );
  }, [getPaymentMethodIcon, getPartnerNameBadge, data, i18n.resolvedLanguage]);

  const renderStatus = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.status)) {
      return (
        <AcCard className={"ac-transaction__column"}>
          <AcRichContent
            content={`<p><em>${t(
              "no-status-information",
              "No status information available",
              { ns: "transactions" }
            )}</em></p>`}
          />
        </AcCard>
      );
    }

    const { created, updated, status, escrow, category, type, uid } = data;
    let transaction_type = null;
    switch (category) {
      case KEYS.ESCROW:
        transaction_type = t("category.escrow", "Escrow transaction", {
          ns: "transactions",
        });
        break;

      default:
        if (type === KEYS.INCOMING) {
          transaction_type = t("category.incoming", "Incoming transaction", {
            ns: "transactions",
          });
        } else if (type === KEYS.OUTGOING) {
          transaction_type = t("category.outgoing", "Outgoing transaction", {
            ns: "transactions",
          });
        }
    }

    const date_format =
      window.outerWidth <= 768
        ? DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES_SHORT
        : DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES;

    let statusLabel = `${t("created-at", "Created at", { ns: "common" })}`;
    let statusDate = AcFormatDate(created * 1000, null, date_format);

    if (data && data.statuses && data.statuses.length > 0) {
      const sorted = data.statuses.slice().reverse();

      const lastStatus = sorted[0];
      statusLabel = statusLabel = t("status." + lastStatus.status, "Status", {
        ns: "transactions",
      });
      statusDate = AcFormatDate(lastStatus.created * 1000, null, date_format);
    }

    const result = {
      details: [],
    };

    if (AcIsSet(uid)) {
      result.details.push({
        label: `${t("uid", "Unique identifier", { ns: "common" })}`,
        value: (
          <AcCopyText
            t={t}
            value={uid}
            title={t("copy-uid", "Copy UID to clipboard", { ns: "common" })}
            id={"ac-tra-uid"}
            fixed
          >
            {uid}
          </AcCopyText>
        ),
      });
    }

    result.details.push(
      {
        label: `${t("status-label", "Status", { ns: "common" })}`,
        value: getStatusBadge,
      },
      {
        label: statusLabel,
        value: statusDate,
      }
    );

    if (
      category &&
      category === KEYS.ESCROW &&
      AcIsSet(escrow?.end) &&
      status === KEYS.RESERVED
    ) {
      result.details.push({
        label: `${t("reserved-until", "Reserved until", {
          ns: "transactions",
        })}`,
        value: AcFormatDate(escrow.end * 1000, null, date_format),
      });
    }

    if (AcIsSet(transaction_type)) {
      result.details.push({
        label: `${t("type", "Type", { ns: "common" })}`,
        value: transaction_type,
      });
    }

    return (
      <AcCard className={"ac-transaction__column h-padding-0"}>
        <div
          className={
            "h-padding-right-20 h-padding-top-20 h-padding-bottom-20 transaction-details-wrap"
          }
        >
          <AcHeading theme={THEMES.PITCH} rank={5} tag={"h3"}>
            {t("details", "Details", { ns: "common" })}
          </AcHeading>
          <AcDefinitionList items={result.details} />
          <AcDivider className={"h-margin-top-10"} theme={THEMES.EGGWHITE} />
        </div>
        <AcAccordion
          open={accordionOpen}
          limited
          title={t("changelog.changelog", "Changelog", { ns: "transactions" })}
          onClick={() => {
            setAccordionOpen(!accordionOpen);
          }}
          theme={THEMES.WHITE}
          titleClassName={"transaction-details-changelog"}
          childrenClassName={
            "h-left-0 h-padding-y-0 h-padding-right-0 transaction-details-changelog"
          }
          className={"h-height-100-p"}
        >
          <AcTransactionDetailsChangelog statuses={data.statuses} />
        </AcAccordion>
      </AcCard>
    );
  }, [data, data.statuses, getStatusBadge, i18n.resolvedLanguage]);

  const renderRefundDetail = (refundDetails) => {
    const date_format =
      window.outerWidth <= 768
        ? DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES_SHORT
        : DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES;

    let label = t("date-paid", "Date paid", { ns: "transactions" });
    if (refundDetails.paid) {
      label = AcGetDateIsInFuture(moment(refundDetails.paid * 1000))
        ? t("date-planned", "Date planned", { ns: "transactions" })
        : t("date-paid", "Date paid", { ns: "transactions" });
    }

    const type = KEYS.MOVEMENT;
    let data = [];
    data[KEYS.UID] = refundDetails.uid;

    const route = AcFormatInternalURI(data, {
      field: KEYS.UID,
      type: type,
    });

    // TODO currency
    const info = [
      {
        label: `${t("amount", "Amount", { ns: "transactions" })}`,
        value: AcFormatCurrencyAmount(
          refundDetails.amount,
          current_partner_currency,
          i18n.resolvedLanguage,
          true
        ),
      },
      {
        label: label,
        value: refundDetails.paid
          ? AcFormatDate(refundDetails.paid * 1000, null, date_format)
          : t("not-paid-yet", "Not paid yet.", { ns: "transactions" }),
      },
      {
        label: `${t("details", { ns: "common" })}`,
        value: (
          <Link className={"ac-link"} target="_blank" to={route}>
            {refundDetails.uid}
            <AcIcon className={"h-margin-x-0"} icon={ICONS.OPEN_IN_NEW} />
          </Link>
        ),
      },
    ];

    return (
      <AcCard className={"ac-transaction__column"}>
        <AcHeading rank={5} theme={THEMES.PITCH}>
          {t("type.refund", { ns: "transactions" })}
        </AcHeading>
        <AcDefinitionList items={info} />
      </AcCard>
    );
  };

  const renderRefundData = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.refunds)) {
      return;
    }

    return (
      <>
        {data.refunds.map((refund) => {
          return renderRefundDetail(refund);
        })}
      </>
    );
  }, [data, data.refunds, i18n.resolvedLanguage]);

  const renderProducts = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.order)) {
      return (
        <AcEmptyBlock
          message={t("no-products-available", "No product details available", {
            ns: "transactions",
          })}
          border={false}
        />
      );
    }

    const collection = data.order.products;
    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const item = collection[n];

      // Todo currency from product?
      const price = AcFormatCurrencyAmount(
        item.price * item.quantity,
        current_partner_currency,
        i18n.resolvedLanguage,
        true
      );
      // const price = AcFormatCurrencyAmount((item.price.amount * item.quantity), item.price.currency, i18n.resolvedLanguage, true);

      let label = item.quantity === 1 ? "" : item.quantity + "x ";
      label += AcSanitize(item.name);

      const object = {
        label: label,
        value: price,
      };

      result.push(object);
    }

    return <AcDefinitionList items={result} />;
  }, [data, i18n.resolvedLanguage, current_partner_currency]);

  const renderShippingDetails = useMemo(() => {
    if (
      !AcIsSet(data) ||
      !AcIsSet(data.tracking) ||
      data.tracking.length === 0
    ) {
      return null;
    }

    const { tracking } = data;
    const trackingObject = tracking[tracking.length - 1];
    const {
      tracking_destination: {
        address_line_1,
        address_line_2,
        city,
        country,
        housenumber,
        housenumber_addition,
        zipcode,
      },
      shipped,
      tracking_number,
      tracking_carrier,
    } = trackingObject;

    let result = [
      {
        label: `${t("track-and-trace", "Track & Trace", {
          ns: "transactions",
        })}`,
        value: "-",
      },
      {
        label: `${t("carrier", "Carrier", { ns: "transactions" })}`,
        value: "-",
      },
      {
        label: `${t("shipping-date", "Shipping date", { ns: "transactions" })}`,
        value: "-",
      },
      {
        label: `${t("address", "Address", { ns: "transactions" })}`,
        value: ["", "", ""],
      },
    ];
    let address = ["", "", ""];

    if (AcIsSet(shipped)) {
      result[2].value = AcFormatDate(
        shipped * 1000,
        null,
        DATETIME_FORMATS.FULL_DATE
      );
    }

    if (AcIsSet(tracking_number)) {
      result[0].value = (
        <AcCopyText
          t={t}
          value={tracking_number}
          title={t("copy-track-and-trace", "Copy Track & Trace to clipboard", {
            ns: "transactions",
          })}
          fixed
        >
          {tracking_number}
        </AcCopyText>
      );
    }

    if (AcIsSet(tracking_carrier) && AcIsSet(tracking_carrier.name)) {
      let name = "-";

      switch (tracking_carrier.name) {
        case "postnl":
          name = "PostNL";
          break;
        case "dhl-de":
        case "dhl-nl":
          name = "DHL";
          break;
        default:
          name = tracking_carrier.name;
          break;
      }

      result[1].value = name;
    }

    if (AcIsSet(address_line_1)) {
      address[0] = `${address[0]} ${address_line_1}`;

      if (AcIsSet(housenumber)) {
        address[0] = `${address[0]} ${housenumber}`;
        if (AcIsSet(housenumber_addition)) {
          address[0] = `${address[0]}${housenumber_addition}`;
        }
      }
    }

    if (AcIsSet(zipcode)) {
      address[1] = `${address[1]} ${zipcode}`;
    }
    if (AcIsSet(city)) {
      address[1] = `${address[1]} ${city}`;
    }
    if (AcIsSet(country)) {
      address[2] = `${address[2]} ${country}`;
    }

    result[3].value = address.join("<br/>");

    return <AcDefinitionList items={result} />;
  }, [data, i18n.resolvedLanguage]);

  const renderTransactionBreakdown = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.fees)) {
      return (
        <AcRichContent
          content={`<p><em>${t(
            "no-transaction-details",
            "No transaction details available",
            { ns: "transactions" }
          )}</em></p>`}
        />
      );
    }

    const paid_amount = data.amount;
    const transaction_fees = data.amount - data.fees.payable_amount;
    const received_amount = data.fees.payable_amount;

    const formatted = {
      //todo currency
      paid_amount: AcFormatCurrencyAmount(
        paid_amount,
        current_partner_currency,
        i18n.resolvedLanguage,
        true
      ),
      transaction_fees: AcFormatCurrencyAmount(
        transaction_fees,
        current_partner_currency,
        i18n.resolvedLanguage,
        true
      ),
      received_amount: AcFormatCurrencyAmount(
        received_amount,
        current_partner_currency,
        i18n.resolvedLanguage,
        true
      ),
    };

    const result = [
      {
        label: `${t("paid-amount", "Paid amount", { ns: "transactions" })}`,
        value: formatted.paid_amount,
      },
      {
        label: `${t("transaction-fees", "Transaction fees", {
          ns: "transactions",
        })}`,
        value: formatted.transaction_fees,
      },
      {
        label: `<strong>${t("amount", { ns: "transactions" })}</strong>`,
        value: `<strong>${formatted.received_amount}</strong>`,
      },
    ];
    return <AcDefinitionList items={result} />;
  }, [data, i18n.resolvedLanguage]);

  const renderPayoutDetail = (payoutDetails) => {
    const receiver = [
      {
        label: `<strong>${t("recipient", "Recipient", {
          ns: "transactions",
        })}</strong>`,
        value: "",
      },
      {
        label: `${t("name", "Name", { ns: "transactions" })}`,
        value: payoutDetails.receiver_details?.name,
      },
      {
        label: `${t("account", "Account", { ns: "transactions" })}`,
        value: payoutDetails.receiver_details?.account,
      },
    ];

    const date_format =
      window.outerWidth <= 768
        ? DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES_SHORT
        : DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES;

    let label = t("date-paid", "Date paid", { ns: "transactions" });
    if (payoutDetails.paid) {
      label = AcGetDateIsInFuture(moment(payoutDetails.paid * 1000))
        ? t("date-planned", "Date planned", { ns: "transactions" })
        : t("date-paid", "Date paid", { ns: "transactions" });
    }

    const other = [
      {
        label: `${t("amount", "Amount", { ns: "transactions" })}`,
        value: AcFormatCurrencyAmount(
          payoutDetails.amount,
          payoutDetails.currency,
          i18n.resolvedLanguage,
          true
        ),
      },
      {
        label: label,
        value: payoutDetails.paid
          ? AcFormatDate(payoutDetails.paid * 1000, null, date_format)
          : t("not-paid-yet", "Not paid yet.", { ns: "transactions" }),
      },
      {
        label: `${t("description", "Description", { ns: "transactions" })}`,
        value: payoutDetails.description,
      },
    ];

    return (
      <AcCard className={"ac-transaction__column"}>
        <AcHeading rank={5} theme={THEMES.PITCH}>
          {t("payout", "Payout", { ns: "transactions" })}
        </AcHeading>
        <AcDefinitionList items={other} />
        {payoutDetails.receiver_details && (
          <AcDefinitionList items={receiver} />
        )}
      </AcCard>
    );
  };

  const renderPayoutDetails = useMemo(() => {
    if (!AcIsSet(current_payouts) || is_loading_payouts) {
      return;
    }

    return (
      <>
        {current_payouts.map((payout) => {
          return renderPayoutDetail(payout);
        })}
      </>
    );
  }, [current_payouts, is_loading_payouts, i18n.resolvedLanguage]);

  const renderImage = useMemo(() => {
    return (
      <AcImage
        wrpClassName={"ac-transaction__hero"}
        source={TransactionHero1x}
        key={AcUUID()}
        transparent
      />
    );
  }, []);

  return (
    <div className={getMainClassNames}>
      <AcCard className={"ac-transaction__column ac-transaction__column--big"}>
        {renderImage}
        {renderMainFeatures}
      </AcCard>

      {renderPayoutDetails}

      {renderStatus}

      <AcCard className={"ac-transaction__column"}>
        <AcHeading theme={THEMES.PITCH} rank={5} tag={"h3"}>
          {t("breakdown", "Breakdown", { ns: "transactions" })}
        </AcHeading>

        {renderTransactionBreakdown}
      </AcCard>

      <AcCard className={"ac-transaction__column"}>
        <AcHeading theme={THEMES.PITCH} rank={5} tag={"h3"}>
          {t("products", "Products", { ns: "transactions" })}
        </AcHeading>
        {renderProducts}
      </AcCard>

      {renderRefundData}

      {renderShippingDetails && (
        <AcCard className={"ac-transaction__column"}>
          <AcHeading theme={THEMES.PITCH} rank={5} tag={"h3"}>
            {t("shipping", "Shipping", { ns: "transactions" })}
          </AcHeading>
          {renderShippingDetails}
        </AcCard>
      )}
    </div>
  );
};

export default withTranslation(["transactions", "titles", "common"])(
  withStore(observer(AcTransactionDetailsDetailsTab))
);
